import axios from 'axios';
import toast from 'react-hot-toast';
import { normalizeError } from '../utils/normalizeError';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '/api/v1',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Track inflight requests to deduplicate error toasts
const inflight = new Set();

// Generate unique key for request deduplication
const keyOf = (cfg) => {
    const method = cfg.method?.toLowerCase() || 'get';
    const url = cfg.url || '';
    const params = cfg.params ? JSON.stringify(cfg.params) : '';
    const data = cfg.data ? JSON.stringify(cfg.data) : '';
    return `${method}:${url}:${params}:${data}`;
};

// Request interceptor for authentication
api.interceptors.request.use(
    (config) => {
        // Add authentication token
        let loginData = null;
        try {
            const storageData = localStorage.getItem('loginSuccessNeurulaHealth');
            if (storageData) {
                loginData = JSON.parse(storageData);
            }
        } catch (error) {
            console.error("Error parsing login data from localStorage:", error);
        }
        
        if (loginData?.authToken) {
            config.headers.Authorization = `Bearer ${loginData.authToken}`;
        }
        
        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() };
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for global error handling
api.interceptors.response.use(
    (response) => {
        // Success response - no error handling needed
        return response;
    },
    (error) => {
        // Skip error handling for cancelled requests
        if (error?.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }
        
        const status = error?.response?.status || 0;
        
        // Only handle HTTP errors (4xx, 5xx)
        if (status >= 400) {
            const config = error.config || {};
            
            // Check if global toast should be suppressed
            if (!config.headers?.['x-suppress-global-toast']) {
                const requestId = keyOf(config);
                
                // Deduplicate errors - only show one toast per unique request
                if (!inflight.has(requestId)) {
                    inflight.add(requestId);
                    
                    // Normalize error and show toast
                    const normalized = normalizeError(error);
                    
                    // Don't show toast for validation errors (422) as they should be handled inline by components
                    // Also check if request is being handled with suppression header
                    // Special handling for 403 (permission) errors - use detail directly
                    if (status === 403) {
                        const permissionMessage = error?.response?.data?.detail || normalized.message;
                        toast.error(permissionMessage, {
                            id: requestId,
                            duration: 6000  // Longer duration for permission errors
                        });
                    } else if (status !== 422) {
                        toast.error(normalized.message, {
                            id: requestId,
                            duration: 5000
                        });
                    } else {
                        // Log validation errors for debugging but don't show toast
                        console.log('🔔 422 validation error suppressed from global toast:', normalized);
                    }
                    
                    // Clean up the inflight tracker after toast duration
                    setTimeout(() => inflight.delete(requestId), 4000);
                }
            }
        }
        
        return Promise.reject(error);
    }
);

export default api;